import { trackingService } from "./service";
import { compactTrackingObject, getSourceSectionFromPath } from "./utils";

const buildSourceSection = (sourceSurface, route) => {
  if (sourceSurface) {
    return sourceSurface;
  }

  if (route) {
    return getSourceSectionFromPath(String(route).split("?")[0]);
  }

  const fallbackPathname = typeof window !== "undefined" ? window.location.pathname : "";
  return getSourceSectionFromPath(fallbackPathname);
};

const ENTITY_SEARCH_EVENT_TYPES = {
  competition: "competition_searched",
  player: "player_searched",
  team: "team_searched",
};

const ENTITY_SELECTION_EVENT_TYPES = {
  competition: "competition_search_result_selected",
  player: "player_search_result_selected",
  team: "team_search_result_selected",
};

const ENTITY_VIEW_EVENT_TYPES = {
  competition: "competition_viewed",
  player: "player_viewed",
  team: "team_viewed",
};

const normalizeEntityType = (entityType = "") => {
  const normalized = String(entityType || "").toLowerCase();
  if (normalized === "competitions") return "competition";
  if (normalized === "players") return "player";
  if (normalized === "teams") return "team";
  return normalized;
};

const getEntityLabel = (entity = {}) =>
  entity?.display_name ||
  entity?.full_name ||
  [entity?.name, entity?.surname].filter(Boolean).join(" ").trim() ||
  entity?.title ||
  entity?.short_name ||
  null;

const summarizeSearchResults = (results = []) =>
  (Array.isArray(results) ? results : []).slice(0, 10).map((entity) =>
    compactTrackingObject({
      id: entity?.id,
      label: getEntityLabel(entity),
    })
  );

export const trackPageView = (location) =>
  trackingService.trackPageView({
    pathname: location?.pathname || "",
    search: location?.search || "",
  });

export const trackCustomEvent = ({
  action,
  entityId = null,
  entityLabel = null,
  entityType = null,
  eventType,
  metadata = {},
  route = null,
  sourceSurface = null,
}) =>
  trackingService.track({
    action,
    entityId,
    entityLabel,
    entityType,
    eventType,
    metadata: compactTrackingObject(metadata),
    route,
    sourceSection: buildSourceSection(sourceSurface, route),
  });

export const trackEntitySearch = ({
  entityType,
  metadata = {},
  query,
  resultCount = null,
  results = [],
  route = null,
  sourceSurface = null,
}) => {
  const normalizedEntityType = normalizeEntityType(entityType);
  const normalizedQuery = String(query || "").trim();

  if (!ENTITY_SEARCH_EVENT_TYPES[normalizedEntityType] || !normalizedQuery) {
    return null;
  }

  const summarizedResults = summarizeSearchResults(results);

  return trackCustomEvent({
    action: "search",
    entityLabel: normalizedQuery,
    entityType: normalizedEntityType,
    eventType: ENTITY_SEARCH_EVENT_TYPES[normalizedEntityType],
    metadata: {
      query: normalizedQuery,
      result_count: resultCount ?? (Array.isArray(results) ? results.length : null),
      results: summarizedResults,
      source_surface: sourceSurface,
      ...metadata,
    },
    route,
    sourceSurface,
  });
};

export const trackEntitySearchResultSelected = ({
  entity,
  entityId = null,
  entityLabel = null,
  entityType,
  metadata = {},
  query = null,
  route = null,
  sourceSurface = null,
}) => {
  const normalizedEntityType = normalizeEntityType(entityType);

  if (!ENTITY_SELECTION_EVENT_TYPES[normalizedEntityType]) {
    return null;
  }

  return trackCustomEvent({
    action: "select_search_result",
    entityId: entityId ?? entity?.id,
    entityLabel: entityLabel || getEntityLabel(entity),
    entityType: normalizedEntityType,
    eventType: ENTITY_SELECTION_EVENT_TYPES[normalizedEntityType],
    metadata: {
      query: query ? String(query).trim() : null,
      source_surface: sourceSurface,
      ...metadata,
    },
    route,
    sourceSurface,
  });
};

export const trackEntityView = ({
  entity,
  entityId = null,
  entityLabel = null,
  entityType,
  metadata = {},
  route = null,
  sourceSurface = null,
}) => {
  const normalizedEntityType = normalizeEntityType(entityType);

  if (!ENTITY_VIEW_EVENT_TYPES[normalizedEntityType]) {
    return null;
  }

  return trackCustomEvent({
    action: "view",
    entityId: entityId ?? entity?.id,
    entityLabel: entityLabel || getEntityLabel(entity),
    entityType: normalizedEntityType,
    eventType: ENTITY_VIEW_EVENT_TYPES[normalizedEntityType],
    metadata: {
      source_surface: sourceSurface,
      ...metadata,
    },
    route,
    sourceSurface,
  });
};

export const trackFavoriteChange = ({
  favoriteStateAfter,
  favoriteStateBefore,
  metadata = {},
  playerId,
  playerName = null,
  sourceSurface = null,
}) =>
  trackCustomEvent({
    action: "favorite_change",
    entityId: playerId,
    entityLabel: playerName,
    entityType: "player",
    eventType:
      sourceSurface === "smrt_search"
        ? "smrt_search_player_favorited"
        : favoriteStateAfter
          ? "favorite_added"
          : "favorite_removed",
    metadata: {
      favorite_state_after: favoriteStateAfter,
      favorite_state_before: favoriteStateBefore,
      source_surface: sourceSurface,
      ...metadata,
    },
    sourceSurface,
  });

export const trackCompareAction = ({
  action = "compare_change",
  compareItemId = null,
  metadata = {},
  playerId = null,
  playerName = null,
  sourceSurface = null,
}) =>
  trackCustomEvent({
    action,
    entityId: compareItemId || playerId,
    entityLabel: playerName,
    entityType: compareItemId ? "compare_entry" : "player",
    eventType:
      sourceSurface === "smrt_search" && action === "add"
        ? "smrt_search_player_compared"
        : action === "add"
          ? "compare_player_added"
          : action === "remove"
            ? "compare_player_removed"
            : action === "season_change"
              ? "compare_player_season_changed"
              : action === "search"
                ? "compare_search_performed"
                : action === "export"
                  ? "compare_export_requested"
                  : "compare_players_loaded",
    metadata: {
      compare_item_id: compareItemId,
      source_surface: sourceSurface,
      ...metadata,
    },
    sourceSurface,
  });

export const trackShadowTeamAction = ({
  action = "update",
  metadata = {},
  playerId = null,
  playerName = null,
  shadowTeamId = null,
  shadowTeamName = null,
  sourceSurface = null,
}) =>
  trackCustomEvent({
    action,
    entityId: playerId || shadowTeamId,
    entityLabel: playerName || shadowTeamName,
    entityType: playerId ? "player" : "shadow_team",
    eventType:
      sourceSurface === "smrt_search" && action === "player_add"
        ? "smrt_search_player_added_to_shadow_team"
        : action === "search"
          ? "shadow_team_search_performed"
          : action === "create"
            ? "shadow_team_created"
            : action === "update"
              ? "shadow_team_updated"
              : action === "delete"
                ? "shadow_team_deleted"
                : action === "player_add"
                  ? "shadow_team_player_added"
                  : action === "player_remove"
                    ? "shadow_team_player_removed"
                    : action === "player_move"
                      ? "shadow_team_player_moved"
                      : "shadow_team_export_requested",
    metadata: {
      shadow_team_id: shadowTeamId,
      shadow_team_name: shadowTeamName,
      source_surface: sourceSurface,
      ...metadata,
    },
    sourceSurface,
  });

export const trackPlaylistAction = ({
  action = "open",
  entityId = null,
  entityLabel = null,
  entityType = "playlist",
  metadata = {},
  sourceSurface = null,
}) =>
  trackCustomEvent({
    action,
    entityId,
    entityLabel,
    entityType,
    eventType:
      action === "list_view"
        ? "playlist_list_viewed"
        : action === "open"
          ? sourceSurface === "shared_playlist"
            ? "shared_playlist_opened"
            : "playlist_opened"
          : action === "create"
            ? "playlist_created"
            : action === "video_add"
              ? sourceSurface === "shared_playlist"
                ? "shared_playlist_clip_saved"
                : "playlist_video_added"
              : action === "video_remove"
                ? "playlist_video_removed"
                : action === "share"
                  ? "playlist_shared"
                  : sourceSurface === "shared_playlist"
                    ? "shared_playlist_video_played"
                    : "playlist_video_played",
    metadata: {
      source_surface: sourceSurface,
      ...metadata,
    },
    sourceSurface,
  });

export const trackVideoAction = ({
  action = "play",
  entityId = null,
  entityLabel = null,
  entityType = "video",
  metadata = {},
  sourceSurface = null,
}) =>
  trackCustomEvent({
    action,
    entityId,
    entityLabel,
    entityType,
    eventType:
      action === "play"
        ? "video_play_started"
        : action === "pause"
          ? "video_paused"
          : action === "seek"
            ? "video_seeked"
            : action === "complete"
              ? "video_completed"
              : action === "download"
                ? "video_download_requested"
                : action === "screenshot"
                  ? "video_screenshot_requested"
                  : action === "clip_mark_start"
                    ? "video_clip_mark_started"
                    : action === "clip_mark_complete"
                      ? "video_clip_mark_completed"
                      : "video_clip_saved_to_playlist",
    metadata: {
      source_surface: sourceSurface,
      ...metadata,
    },
    sourceSurface,
  });

export const resetTrackingForTests = () => trackingService.resetForTests();
export const getTrackingQueueForTests = () => trackingService.getQueueSnapshotForTests();
